<template>
  <v-dialog v-model="open" max-width="580" persistent>
    <v-card>
      <v-card-title class="font-weight-black d-flex align-center">
        <v-icon color="primary" class="mr-2">fas fa-user-plus</v-icon>
        Cliente para cotizar
      </v-card-title>
      <v-card-subtitle class="pb-2">
        Crea el cliente y su contacto para asignarlos directamente a la cotización.
      </v-card-subtitle>

      <v-card-text class="pt-2">
        <v-form ref="form" v-model="formValid" lazy-validation @submit.prevent="submit">
          <!-- Información de la Empresa -->
          <div class="caption grey--text font-weight-bold text-uppercase mb-2">
            Datos de la Empresa / Cliente
          </div>

          <v-text-field
            v-model="form.rut"
            :label="`${taxIdLabel} *`"
            :placeholder="isChile ? 'Ej: 12345678-9' : 'Número de identificación fiscal'"
            outlined
            dense
            :rules="[rules.taxId]"
            :error-messages="rutError"
            :loading="checkingRut"
            @blur="checkExistingRut"
          />

          <v-text-field
            v-model="form.name"
            label="Razón Social *"
            placeholder="Nombre legal o comercial de la empresa"
            outlined
            dense
            :rules="[rules.required]"
          />

          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.fantasy"
                label="Nombre de Fantasía (Opcional)"
                placeholder="Marca o sucursal"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.giro"
                label="Giro *"
                placeholder="Giro o actividad económica"
                outlined
                dense
                :rules="[rules.required]"
              />
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.phone"
                label="Teléfono Empresa"
                placeholder="+56 9..."
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="form.seller"
                :items="sellerItems"
                item-text="text"
                item-value="id"
                label="Vendedor *"
                outlined
                dense
                return-object
                :rules="[rules.required]"
              />
            </v-col>
          </v-row>

          <!-- Información del Contacto (Obligatorio) -->
          <v-divider class="my-3"></v-divider>
          <div class="subtitle-2 font-weight-bold primary--text d-flex align-center mb-2">
            <v-icon small color="primary" class="mr-1">fas fa-id-badge</v-icon>
            Datos del Contacto (Obligatorio)
          </div>

          <v-text-field
            v-model="form.contact_name"
            label="Nombre del Contacto *"
            placeholder="Nombre y apellido del contacto"
            outlined
            dense
            :rules="[rules.required]"
          />

          <v-text-field
            v-model="form.contact_email"
            label="Email del Contacto *"
            placeholder="ejemplo@dominio.com"
            outlined
            dense
            :rules="[rules.email]"
          />

          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.contact_phone"
                label="Teléfono Contacto (Opcional)"
                placeholder="+56 9..."
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.contact_cargo"
                label="Cargo (Opcional)"
                placeholder="Ej: Adquisiciones, Gerente"
                outlined
                dense
              />
            </v-col>
          </v-row>
        </v-form>

        <!-- Aviso si ya existe el identificador fiscal -->
        <v-alert v-if="existing" dense text type="info" class="mt-2 mb-0">
          Ya existe un cliente con este {{ taxIdLabel }}: <strong>{{ existing.name }}</strong>.
          <div class="caption grey--text text--darken-2 mt-1">
            Puedes seleccionar el existente o continuar para registrar esta nueva sucursal con el mismo {{ taxIdLabel }}.
          </div>
        </v-alert>
      </v-card-text>

      <v-card-actions class="px-6 pb-4">
        <v-spacer />
        <v-btn text :disabled="saving" @click="close">Cancelar</v-btn>
        <v-btn
          v-if="existing"
          outlined
          color="primary"
          :disabled="saving"
          class="mr-2"
          @click="useExisting"
        >
          Usar cliente existente
        </v-btn>
        <v-btn
          color="primary"
          depressed
          :loading="saving"
          @click="submit"
        >
          {{ existing ? 'Crear nueva sucursal' : 'Crear y continuar' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { createCustomer, fetchCustomers, getCustomer } from '@/api/customers'
import { fetchSellers } from '@/api/auth'
import notifications from '@/utils/notifications'
import getInternacional from '@/utils/internacional'
import { validaRUT } from '@/utils/rut_validator'

export default {
  name: 'QuickCustomerDialog',
  props: {
    value: { type: Boolean, default: false },
    prefill: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      formValid: true,
      saving: false,
      checkingRut: false,
      existing: null,
      rutError: '',
      sellerItems: [],
      form: this.emptyForm(),
      rules: {
        required: (v) => !!(v && (typeof v === 'string' ? v.trim() : true)) || 'Este campo es obligatorio',
        taxId: (v) => {
          const val = (v || '').trim()
          if (!val) return `${this.taxIdLabel} es obligatorio`
          if (this.rutValidationActive) {
            const clean = val.replace(/\./g, '')
            return validaRUT(clean) || 'RUT inválido (ej: 12345678-9)'
          }
          return true
        },
        email: (v) => {
          const val = (v || '').trim()
          if (!val) return 'El email del contacto es obligatorio'
          const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          return regex.test(val) || 'Ingrese un correo electrónico válido'
        },
      },
    }
  },
  computed: {
    open: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    company() {
      return this.$store.getters.getCompany || {}
    },
    countryCode() {
      return ((this.company && this.company.country_code) || 'cl').toLowerCase()
    },
    isChile() {
      return this.countryCode === 'cl'
    },
    rutValidationActive() {
      return !!(this.company && this.company.rut_validation_active)
    },
    taxIdLabel() {
      const config = getInternacional(this.countryCode)
      return config && config.tax_id_label ? config.tax_id_label : (this.isChile ? 'RUT' : 'Identificación Fiscal')
    },
  },
  watch: {
    value(val) {
      if (val) this.resetForm()
    },
  },
  methods: {
    emptyForm() {
      const user = this.$store.state.user || {}
      return {
        rut: '',
        name: '',
        fantasy: '',
        giro: '',
        phone: '',
        seller: user.id ? user : null,
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        contact_cargo: '',
      }
    },
    looksLikeRut(value) {
      const digits = ((value || '').match(/\d/g) || []).length
      return digits >= 7
    },
    resetForm() {
      this.existing = null
      this.rutError = ''
      this.checkingRut = false
      this.form = this.emptyForm()
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
      const prefill = this.prefill || {}
      const search = (prefill.search || prefill.name || '').trim()
      if (prefill.rut) this.form.rut = prefill.rut
      else if (this.looksLikeRut(search)) this.form.rut = search
      if (prefill.name) this.form.name = prefill.name
      else if (search && !this.looksLikeRut(search)) this.form.name = search
      if (prefill.fantasy) this.form.fantasy = prefill.fantasy
      if (prefill.giro) this.form.giro = prefill.giro
      if (prefill.phone) this.form.phone = prefill.phone
      if (prefill.contact_name) this.form.contact_name = prefill.contact_name
      if (prefill.contact_email || prefill.email) this.form.contact_email = prefill.contact_email || prefill.email
      if (prefill.contact_phone) this.form.contact_phone = prefill.contact_phone
      if (prefill.contact_cargo) this.form.contact_cargo = prefill.contact_cargo

      if (this.form.seller && this.form.seller.id) {
        const match = this.sellerItems.find((item) => item.id === this.form.seller.id)
        if (match) this.form.seller = match
      }
      if (this.form.rut) this.checkExistingRut()
    },
    close() {
      this.open = false
    },
    splitName(full) {
      const parts = (full || '').trim().split(/\s+/)
      if (!parts[0]) return { first_name: '', last_name: '' }
      return { first_name: parts[0], last_name: parts.slice(1).join(' ') }
    },
    formatError(e) {
      const data = e && e.response && e.response.data
      if (!data) return 'No se pudo crear el cliente'
      if (typeof data === 'string') return data
      if (data.error) return data.error
      if (data.detail) return typeof data.detail === 'string' ? data.detail : 'No se pudo crear el cliente'
      const parts = []
      Object.keys(data).forEach((key) => {
        const val = data[key]
        if (Array.isArray(val)) parts.push(val.join(' '))
        else if (typeof val === 'string') parts.push(val)
      })
      return parts.join(' · ') || 'No se pudo crear el cliente'
    },
    async loadSellers() {
      try {
        const data = await fetchSellers()
        const list = Array.isArray(data) ? data : (data && data.results) || []
        const items = list.map((s) => ({
          id: s.id,
          text: `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.email,
          ...s,
        }))
        const user = this.$store.state.user
        if (user && user.id && !items.some((item) => item.id === user.id)) {
          items.unshift({
            id: user.id,
            text: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Yo',
            ...user,
          })
        }
        this.sellerItems = items
        if (this.form.seller && this.form.seller.id) {
          const match = items.find((item) => item.id === this.form.seller.id)
          if (match) this.form.seller = match
        }
      } catch (e) {
        this.sellerItems = []
      }
    },
    async checkExistingRut() {
      let rut = (this.form.rut || '').trim()
      if (this.isChile && rut) {
        rut = rut.replace(/\./g, '')
        this.form.rut = rut
      }
      this.existing = null
      this.rutError = ''
      if (!rut) return

      if (this.isChile && !validaRUT(rut)) {
        this.rutError = 'RUT inválido (ej: 12345678-9)'
        return
      }

      this.checkingRut = true
      try {
        const data = await fetchCustomers('', 1, 5, '', '', '', rut)
        const rows = data.results || []
        this.existing = rows[0] || null
      } catch (e) {
        this.existing = null
      } finally {
        this.checkingRut = false
      }
    },
    async useExisting() {
      if (!this.existing) return
      this.saving = true
      try {
        let fullExisting = this.existing
        if (!fullExisting.contacts || !fullExisting.contacts.length) {
          try {
            fullExisting = await getCustomer(this.existing.id)
          } catch (e) {
            fullExisting = this.existing
          }
        }
        const primaryContact = (fullExisting.contacts && fullExisting.contacts[0]) || null
        this.$emit('created', fullExisting, primaryContact)
        notifications.showSuccess('Se seleccionó el cliente existente')
        this.close()
      } finally {
        this.saving = false
      }
    },
    async submit() {
      this.rutError = ''
      if (this.$refs.form && !this.$refs.form.validate()) {
        notifications.showError('Por favor complete todos los campos obligatorios correctamente')
        return
      }

      let taxId = (this.form.rut || '').trim()
      if (this.isChile) {
        taxId = taxId.replace(/\./g, '')
        if (!validaRUT(taxId)) {
          this.rutError = 'RUT inválido (ej: 12345678-9)'
          notifications.showError('El RUT ingresado no es válido')
          return
        }
      }

      this.saving = true
      try {
        const names = this.splitName(this.form.contact_name)
        const payload = {
          rut: taxId,
          name: this.form.name.trim(),
          giro: this.form.giro.trim(),
          fantasy: (this.form.fantasy && this.form.fantasy.trim()) || this.form.name.trim(),
          phone: this.form.phone ? this.form.phone.trim() : '-',
          seller: this.form.seller && this.form.seller.id ? this.form.seller.id : this.form.seller,
          payment: 0,
          com_agencia: 0,
          status: true,
          branch_office: 0,
          contact_data: {
            first_name: names.first_name,
            last_name: names.last_name || '',
            email: (this.form.contact_email || '').trim(),
            phone: (this.form.contact_phone || this.form.phone || '').trim(),
            cargo: (this.form.contact_cargo || '').trim(),
          },
        }

        const created = await createCustomer(payload)
        let fullCreated = created
        if (!fullCreated.contacts || !fullCreated.contacts.length) {
          try {
            fullCreated = await getCustomer(created.id)
          } catch (e) {
            fullCreated = created
          }
        }

        const createdContact =
          (fullCreated.contacts &&
            fullCreated.contacts.find((c) => c.email === payload.contact_data.email)) ||
          (fullCreated.contacts && fullCreated.contacts[fullCreated.contacts.length - 1]) ||
          (fullCreated.contacts && fullCreated.contacts[0]) ||
          null

        this.$emit('created', fullCreated, createdContact)
        notifications.showSuccess('Cliente y contacto creados exitosamente')
        this.close()
      } catch (e) {
        notifications.showError(this.formatError(e))
      } finally {
        this.saving = false
      }
    },
  },
  created() {
    this.loadSellers()
  },
}
</script>
